// src/pages/TungCatcherGamePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Home } from 'lucide-react';

const TungCatcherGamePage = () => {
    const canvasRef = useRef(null);
    const requestRef = useRef();

    // Game State
    const [score, setScore] = useState(0);
    const [missed, setMissed] = useState(0); // [ใหม่] State สำหรับเก็บจำนวนที่พลาด
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);

    // Configuration
    const playerSize = 60; 
    const itemSize = 40;   
    const baseSpeed = 3; 
    const maxMisses = 5; // [ใหม่] จำนวนครั้งที่อนุญาตให้พลาดได้

    // ตัวแปรสำหรับ Logic เกม
    const gameState = useRef({
        playerX: 0, 
        items: [],  
        lastSpawn: 0,
        score: 0,
        missed: 0 // [ใหม่] เก็บใน ref เพื่อคำนวณใน loop
    });

    useEffect(() => {
        const preventDefault = (e) => e.preventDefault();
        document.body.style.overflow = 'hidden';
        document.addEventListener('touchmove', preventDefault, { passive: false });

        return () => {
            document.body.style.overflow = 'auto';
            document.removeEventListener('touchmove', preventDefault);
            cancelAnimationFrame(requestRef.current);
        };
    }, []);

    const startGame = () => {
        setGameStarted(true);
        setGameOver(false);
        setScore(0);
        setMissed(0); // [ใหม่] รีเซ็ตค่าพลาด
        gameState.current = {
            playerX: window.innerWidth / 2,
            items: [],
            lastSpawn: 0,
            score: 0,
            missed: 0 // [ใหม่] รีเซ็ตค่าพลาดใน Logic
        };
        requestRef.current = requestAnimationFrame(gameLoop);
    };

    const gameLoop = (timestamp) => {
        if (!canvasRef.current) return;

        updateGameState(timestamp);
        drawGame();

        if (!gameOver) {
            requestRef.current = requestAnimationFrame(gameLoop);
        }
    };

    const updateGameState = (timestamp) => {
        const canvas = canvasRef.current;
        const state = gameState.current;

        // Difficulty Logic
        const difficultyLevel = Math.floor(state.score / 50);
        const speedBonus = difficultyLevel * 0.5; 
        const spawnRate = Math.max(300, 800 - (difficultyLevel * 20));

        // 1. Spawn Items
        if (timestamp - state.lastSpawn > spawnRate) { 
            const isGoodItem = Math.random() > 0.3; 
            let itemSpeed = (Math.random() * 2) + baseSpeed + speedBonus;
            if (itemSpeed > 15) itemSpeed = 15;

            state.items.push({
                id: Date.now(),
                x: Math.random() * (canvas.width - itemSize),
                y: -50,
                type: isGoodItem ? 'tung' : 'rain',
                speed: itemSpeed
            });
            state.lastSpawn = timestamp;
        }

        // 2. Move Items & Check Collision
        // ใช้ for loop แบบถอยหลัง (Reverse Loop) เพื่อให้ลบ Array ได้ปลอดภัย
        for (let i = state.items.length - 1; i >= 0; i--) {
            const item = state.items[i];
            item.y += item.speed;

            // ตรวจสอบการรับของ (Collision)
            if (
                item.y + itemSize > canvas.height - 100 && 
                item.y < canvas.height - 40 &&
                item.x < state.playerX + playerSize / 2 && 
                item.x + itemSize > state.playerX - playerSize / 2 
            ) {
                if (item.type === 'tung') {
                    state.score += 10;
                    setScore(state.score);
                    state.items.splice(i, 1); // รับได้แล้วลบออก
                } else if (item.type === 'rain') {
                    endGame(); // จบเกมทันที
                    return; 
                }
            }
            // ตรวจสอบของที่ตกเลยขอบจอ (Missed)
            else if (item.y > canvas.height) {
                // [ใหม่] ถ้าเป็น 'ตุง' แล้วปล่อยตก -> นับเป็นพลาด
                if (item.type === 'tung') {
                    state.missed += 1;
                    setMissed(state.missed); // อัปเดต UI
                    
                    // เช็คว่าพลาดเกินกำหนดหรือยัง
                    if (state.missed >= maxMisses) {
                        endGame(); // จบเกมทันที
                        return;
                    }
                }
                state.items.splice(i, 1); // ลบออกจาก Array
            }
        }
    };

    // ฟังก์ชันจบเกมแยกออกมาเพื่อเรียกใช้ซ้ำ
    const endGame = () => {
        setGameOver(true);
        setGameStarted(false);
        cancelAnimationFrame(requestRef.current);
    };

    const drawGame = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const state = gameState.current;
        ctx.font = `${playerSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.fillText('🧺', state.playerX, canvas.height - 50);

        state.items.forEach(item => {
            ctx.font = `${itemSize}px Arial`;
            if (item.type === 'tung') {
                ctx.fillText('🚩', item.x + itemSize / 2, item.y + itemSize); 
            } else {
                ctx.fillText('⛈️', item.x + itemSize / 2, item.y + itemSize); 
            }
        });
    };

    const handleInput = (clientX) => {
        if (!gameStarted || gameOver) return;
        const canvas = canvasRef.current;
        if (canvas) {
            let newX = clientX;
            if (newX < playerSize / 2) newX = playerSize / 2;
            if (newX > canvas.width - playerSize / 2) newX = canvas.width - playerSize / 2;
            gameState.current.playerX = newX;
        }
    };

    return (
        <div className="min-h-screen bg-sky-100 font-sans relative overflow-hidden select-none">
            <div className="absolute top-4 left-4 z-50 flex gap-2">
                <Link
                    to="/games"
                    className="flex items-center justify-center w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full shadow-md text-amber-800 hover:bg-white hover:scale-110 transition-all border border-amber-100"
                    title="กลับไปหน้ารวมเกม"
                >
                    <ChevronLeft className="w-6 h-6" />
                </Link>

                <Link
                    to="/"
                    className="flex items-center justify-center w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full shadow-md text-amber-800 hover:bg-white hover:scale-110 transition-all border border-amber-100"
                    title="กลับหน้าหลักเว็บไซต์"
                >
                    <Home className="w-5 h-5" />
                </Link>
            </div>
           
            {/* HUD Score & Missed */}
            <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
                <div className="bg-white/80 px-4 py-2 rounded-full shadow-md">
                    <span className="font-bold text-sky-800 text-xl">คะแนน: {score}</span>
                </div>
                {/* [ใหม่] แสดงจำนวนพลาด */}
                <div className={`px-4 py-1 rounded-full shadow-md text-sm font-bold ${missed >= maxMisses - 1 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-white/80 text-gray-600'}`}>
                    หลุดมือ: {missed} / {maxMisses}
                </div>
            </div>

            <canvas
                ref={canvasRef}
                width={window.innerWidth}
                height={window.innerHeight}
                className="block touch-none"
                onMouseMove={(e) => handleInput(e.clientX)}
                onTouchMove={(e) => handleInput(e.touches[0].clientX)}
            />

            {(!gameStarted || gameOver) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-20 backdrop-blur-sm">
                    <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-sm animate-bounce-in">
                        {gameOver ? (
                            <>
                                <h2 className="text-3xl font-bold text-red-500 mb-2">จบเกม!</h2>
                                {/* [ใหม่] แสดงข้อความตามสาเหตุที่แพ้ */}
                                <p className="text-gray-600 mb-4">
                                    {missed >= maxMisses 
                                        ? "คุณทำตุงหล่นหายไปเยอะเกินไป" 
                                        : "โดนพายุฝนซัดตุงหายไปหมดเลย"
                                    }
                                </p>
                                <div className="text-4xl font-bold text-sky-600 mb-6">{score} คะแนน</div>
                            </>
                        ) : (
                            <>
                                <h1 className="text-3xl font-bold text-amber-600 mb-2">รับตุงมงคล</h1>
                                <p className="text-gray-500 mb-6">รับ "ตุง" (🚩) ห้ามหล่นเกิน {maxMisses} ครั้ง <br />และระวังอย่าให้โดน "พายุ" (⛈️)</p>
                            </>
                        )}

                        <button
                            onClick={startGame}
                            className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 px-8 rounded-full text-lg shadow-lg transition transform active:scale-95"
                        >
                            {gameOver ? 'เล่นใหม่อีกครั้ง' : 'เริ่มเกม'}
                        </button>
                    </div>
                </div>
            )}

            {gameOver && (
                <div className="absolute bottom-4 w-full text-center px-4 animate-fade-in-up">
                    <div className="bg-white/90 p-3 rounded-lg shadow inline-block max-w-md text-sm text-sky-900">
                        💡 <strong>รู้หรือไม่:</strong> "ตุง" ในภาษาล้านนาหมายถึง "ธง" ชาวล้านนานิยมถวายตุงเพื่อเป็นพุทธบูชา
                    </div>
                </div>
            )}
        </div>
    );
};

export default TungCatcherGamePage;